<template>
  <div>
    <WizardQuestion
      :title="
        getComponentUiText(
          'app-menu-submenu-home-setup-wizard-web-facing-app-title-1',
        )
      "
      :description="
        getComponentUiText(
          'app-menu-submenu-home-setup-wizard-web-facing-app-description-1',
        )
      "
    >
      <WizardRadioQuestion
        :class="['tw-mb-[25px]']"
        :title="
          getComponentUiText(
            'app-menu-submenu-home-setup-wizard-web-facing-app-title-question-1',
          )
        "
        :answer="isServiceName"
        :required="true"
        :disabled="isDisabledChecked"
        @on-update="checkYesNo"
      />
      <div
        v-if="
          statusInPage.statusPage === statusPage.Error &&
          statusInPage.errorMessage
        "
        class="tw-bg-[#FEE5E2] tw-px-[12px] tw-py-[8px] tw-flex tw-items-center tw-mt-[37px] tw-rounded-[6px] tw-gap-[10px]"
      >
        <WarningIcon color="#F7553C" />
        <p
          class="tw-text-[#F7553C] tw-text-[11px] tw-font-[500] tw-leading-[130%] tw-text-start tw-mb-[0px]"
        >
          {{
            getComponentUiText(
              "app-component-wizards-setup-error-tooltip",
              false,
              {
                errorMessage: statusInPage.errorMessage
                  ? ensurePeriod(statusInPage.errorMessage)
                  : "",
              },
            )
          }}
        </p>
      </div>
      <div v-if="isShowDetail">
        <p class="tw-mb-[25px] tw-pt-[12px] tw-leading-[100%]">
          <span class="tw-text-[#231F20] tw-text-[14px] tw-font-medium">{{
            getComponentUiText(
              "app-menu-submenu-home-setup-wizard-web-facing-app-title-2",
            )
          }}</span>
        </p>
        <div
          v-for="(item, index) in formData"
          :key="`${item.timeUpdated}-${index}`"
          class="tw-mb-[10px]"
        >
          <ListWebFacing
            ref="listWebFacingRef"
            :data="{ item: item, index: index }"
            :status-in-page="statusInPage"
            @update:value:store="handleDataWebFacingPage"
          />
        </div>
        <ButtonAddAnother @click="addAnother">
          {{
            getComponentUiText(
              "app-menu-submenu-home-setup-wizard-web-facing-add-another-button-1",
            )
          }}
        </ButtonAddAnother>
      </div>
    </WizardQuestion>
  </div>
</template>

<script lang="ts" setup>
import { ensurePeriod, getComponentUiText } from "@/utils/utilities";
import WizardQuestion from "./wizardQuestion.vue";
import WizardRadioQuestion from "./wizardRadioQuestion.vue";
import { Ref, computed, onMounted, ref, watch } from "vue";

import ButtonAddAnother from "./buttonAddAnother.vue";
import store from "@/store";
import cloneDeep from "lodash/cloneDeep";
import { PropType } from "vue";
import ListWebFacing from "./listWebFacing.vue";
import { useRoute } from "vue-router";
import { WIZARD_STATE_NAME, statusPage } from "@/utils/constants";
import dayjs from "dayjs";
import WarningIcon from "../svgs/warningIcon.vue";

const props = defineProps({
  listWebFacing: {
    type: Array as PropType<any[]>,
    default: () => [],
  },
  statusInPage: {
    type: Object,
    default: () => ({
      statusPage: statusPage.NotChange,
      dataError: [],
      errorMessage: "",
    }),
  },
});
const listWebFacingRef: any = ref([]);

let typeIdSelectedDropdown = "";

const formData: Ref<any> = ref([
  {
    showAccordion: false,
    typeId: "",
    url: "",
    name: "",
    isWebsite: 3,
    isArchived: false,
  },
]);

const isDisabledChecked = computed(() => {
  const setupWizard = store.state.SetupWizard;
  if (setupWizard.valuesWebservies) {
    const isDisabledChecked = setupWizard.valuesWebservies.some(
      (item) => (item.name || item.typeId || item.url) && !item.isArchived,
    );
    return isDisabledChecked ? 1 : false;
  } else {
    return false;
  }
});

const valuesWebservies = computed(() => {
  return store.state.SetupWizard.valuesWebservies;
});

const isServiceName = computed(() => {
  const queryValue = route.query?.type;
  const appName =
    isShowDetail.value === null
      ? formData.value.some(
          (item) => (item.name || item.typeId || item.url) && !item.isArchived,
        )
      : isShowDetail.value;
  return appName || queryValue === WIZARD_STATE_NAME.WEBSERVICES ? 1 : "";
});

watch(props.statusInPage, (value) => {
  if (
    (value.statusPage === statusPage.CallApi ||
      value.statusPage === statusPage.Error) &&
    (props.statusInPage.dataError.length > 1 ||
      (props.statusInPage.dataError.length === 1 &&
        !props.statusInPage.dataError[0].endDate))
  ) {
    formData.value = formData.value.map((item) => {
      item.isCallAPI = true;
      return item;
    });
  }
});

watch(valuesWebservies, (value) => {
  if (value.length === 0) {
    formData.value = [
      {
        showAccordion: false,
        typeId: "",
        url: "",
        name: "",
        isWebsite: 3,
        isArchived: false,
      },
    ];
    return;
  }
  initFormData();
});

watch(
  props.listWebFacing,
  () => {
    formData.value = props.listWebFacing;
  },
  { deep: true },
);

watch(
  formData,
  (newValue, oldValue) => {
    newValue.forEach((item) => {
      if (item.typeId && item.serviceTypeId === "web-nat") {
        item.isStatus = 0;
      } else if (item.typeId && item.serviceTypeId === "web") {
        item.isStatus = 1;
      }
    });
    return emits("update:listWebFacing", newValue);
  },
  { deep: true },
);

const handleDataWebFacingPage = (item) => {
  formData.value[item.index] = item.data;
  emits("changeDataWebFacing", true);
};
const emits = defineEmits([
  "onUpdate",
  "update:answer",
  "update:toggleValue",
  "update:listWebFacing",
  "changeDataWebFacing",
]);

const updateStore = (itemIds) => {
  typeIdSelectedDropdown = itemIds;
  emits("changeDataWebFacing", true);
};
const isShowDetail = ref(null);

const currentOrg = computed(() => store.state.Org.currentOrg);
const route = useRoute();

onMounted(() => {
  const queryValue = route.query?.type;
  if (valuesWebservies.value.length === 0) {
    formData.value = [
      {
        showAccordion: queryValue === WIZARD_STATE_NAME.WEBSERVICES,
        typeId: "",
        url: "",
        name: "",
        isWebsite: 3,
        isArchived: false,
        isStatus: "",
      },
    ];
    if (queryValue === WIZARD_STATE_NAME.WEBSERVICES) {
      checkYesNo(1);
      setTimeout(() => {
        document
          .getElementById(`input-webfacing-${formData.value.length - 1}`)
          ?.focus();
      }, 1000);
    }
    return;
  } else {
    initFormData();
    if (queryValue === WIZARD_STATE_NAME.WEBSERVICES) {
      addAnother(true);
      setTimeout(() => {
        document
          .getElementById(`input-webfacing-${formData.value.length - 1}`)
          ?.focus();
      }, 2000);
    }
  }
});

const initFormData = () => {
  const data = cloneDeep(valuesWebservies.value);
  data.map((item) => {
    item.name = item.description || item.appName;
    item.timeUpdated = dayjs();
    item.isStatus = item.serviceTypeId === "web-nat" ? 0 : 1;
    return item;
  });
  if (valuesWebservies.value.length > 0) {
    formData.value = cloneDeep(data);
    return;
  }
  formData.value = {
    ...cloneDeep(data),
    ...cloneDeep(formData.value),
  };
};

const checkYesNo = (value) => {
  if (value === 1) {
    isShowDetail.value = true;
  } else {
    isShowDetail.value = false;
  }
  emits("onUpdate", value);
};

const addAnother = (isShowAccordion = false) => {
  const objAdd = ref({
    showAccordion: isShowAccordion,
    typeId: "",
    name: "",
    url: "",
    isWebsite: 3,
    isArchived: false,
    isStatus: "",
    isCallAPI: false,
  });

  formData.value.push(objAdd.value);
};

const checkAllValidations = async () => {
  let isAllValid = true,
    listServices = [] as any[];
  for (let index = 0; index < listWebFacingRef.value.length; index++) {
    const test = await listWebFacingRef.value[index].saveChanges();
    if (!test.isValid) {
      isAllValid = false;
    }
    listServices.push(test.finalValue);
  }
  return isAllValid;
};

const onDeleteItem = (index) => {
  formData.value.splice(index, 1);
};

defineExpose({
  checkAllValidations,
  onDeleteItem,
});
</script>
<style scoped>
.enter__name {
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 14px;
  font-family: Poppins;
  color: #211f58;
  width: 98%;
}
::placeholder {
  color: #b7b7b7;
  font-size: 14px;
  font-family: Poppins;
  font-weight: 400;
}
@media screen and (max-width: 590px) {
  .enter__name {
    width: 100%;
  }
}
</style>
